import { FC, useEffect, useRef, useContext, useState } from 'react'
import clsx from 'clsx'

import TranslationPlayer from '../translation-player/translation-player'
import TranslationSoundButton from '../translation-sound-button/translation-sound-button'
import statisticContext from 'context/statisticsContext'
import WebinarSocketContext from 'context/webinarSocketContext'
import PlayerControls from '../controls/player-controls'
import useFullscreen from '../translation-hooks/useFullscreen'
import usePCWindow from 'hooks/usePCWindow'
import useDeviceOrientation, { ScreenOrientation } from 'hooks/useDeviceOrientation'
import useIsHideControls from '../translation-hooks/useIsHideControls'
import useVisibleUnmute from '../useVisibleUnmute'

import { PlayerStatus, statisticEvent } from '../../data'
import { StatusVocabulary } from 'constants/data'
import { IAnyObj } from 'constants/types/common.types'
import { CONTROLS_FIRST_SHOW_TIME, CONTROLS_SHOW_TIME, VkEvents } from './vk-translation-constants'

import iframeStyle from '../../iframe.module.scss'

interface IVkVideo {
  videoLink: string
}

const BUG_COUNTER_LIMIT = 5

const VkTranslation: FC<IVkVideo> = ({ videoLink }) => {
  const isPC = usePCWindow()
  const orientation = useDeviceOrientation()
  const socket = useContext(statisticContext)
  const { webinarStatus } = useContext(WebinarSocketContext)

  const [isMuted, setIsMuted] = useState(true)
  const [scriptLoad, setScriptLoad] = useState(false)
  const [isReady, setIsReady] = useState(false)
  const [isVideoStarted, setIsVideoStarted] = useState(false)
  const [isVideoBug, setIsVideoBug] = useState(false)

  const vkRef = useRef<IAnyObj | null>(null)
  const playerRef = useRef<HTMLIFrameElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const fakeContainerRef = useRef<HTMLDivElement>(null)
  const timeUpdateCounter = useRef(0)

  const setVolume = (value: number) => {
    vkRef.current?.setVolume(Number(value / 100))
  }

  const unMute = () => {
    vkRef.current?.setVolume(0)
    vkRef.current?.mute()

    setTimeout(() => {
      vkRef.current?.setVolume(1)
      vkRef.current?.unmute()
    }, 200)
    setIsMuted(false)
  }

  const onVisible = () => {
    vkRef.current?.seekLive()
    vkRef.current?.play()
    unMute()
  }

  const mute = () => {
    vkRef.current?.setVolume(0)
    vkRef.current?.mute()
    setIsMuted(true)
  }

  const handleClickToggle = () => {
    setIsVideoStarted(true)
    vkRef.current?.play()
    isMuted ? unMute() : mute()
  }

  const isVerticalOrientation = orientation === ScreenOrientation.vertical

  const onPlayVideo = () => {
    socket?.sendEvent?.(statisticEvent[PlayerStatus.PLAYING])
  }

  const blockTopClick = (e: React.MouseEvent<HTMLElement>) => e.stopPropagation()

  const { isFullScreen, dimension, toggleFullScreen } = useFullscreen(containerRef)
  const isHideControls = useIsHideControls(
    fakeContainerRef,
    CONTROLS_SHOW_TIME,
    CONTROLS_FIRST_SHOW_TIME,
    true
  )

  const isWebinarStarted = webinarStatus === StatusVocabulary.ACTIVE

  useEffect(() => {
    if (!window.VK) return

    const vkInstance = window.VK.VideoPlayer(playerRef.current)
    if (!vkInstance) return

    window.vkRef = vkInstance
    vkRef.current = vkInstance

    vkInstance.on(VkEvents.STARTED, onPlayVideo)
    vkInstance.on(VkEvents.TIMEUPDATE, () => {
      timeUpdateCounter.current += 1
      if (timeUpdateCounter.current < BUG_COUNTER_LIMIT) return
      setIsVideoBug(false)
    })
    vkInstance.on(VkEvents.INITED, (e: any) => {
      setTimeout(() => {
        setIsReady(true)
        vkRef.current?.play()
      }, 100)
      if (e.state === VkEvents.UNSTARTED) setIsVideoBug(true)
    })
    vkInstance.on(VkEvents.ENDED, () => socket?.sendEvent?.(statisticEvent[PlayerStatus.END]))
    vkInstance.on(VkEvents.ERROR, () => setIsVideoBug(true))
  }, [scriptLoad])

  useEffect(() => {
    const script = document.createElement('script')
    script.src = 'https://vk.com/js/api/videoplayer.js'
    script.async = true

    script.onload = () => {
      setScriptLoad(true)
    }

    document.body.appendChild(script)

    return () => {
      document.body.removeChild(script)
    }
  }, [])

  useVisibleUnmute(isVideoBug || !isVideoStarted, onVisible, mute)

  return (
    <TranslationPlayer
      isFullScreen={isFullScreen}
      dimension={dimension}
      isPC={isPC}
      isVerticalOrientation={isVerticalOrientation}
      fakeContainerRef={fakeContainerRef}
      containerRef={containerRef}
    >
      <div className={iframeStyle.vkWrapper}>
        <iframe
          src={`${videoLink}&mute=1&js_api=1`}
          width="100%"
          height="100%"
          ref={playerRef}
          className={clsx(iframeStyle.inner, {
            [iframeStyle.allPointerEvents]: isVideoBug,
          })}
          allow="autoplay; screen-wake-lock"
          allowFullScreen={false}
        />
      </div>

      <div className={clsx(iframeStyle.blockLine, iframeStyle.topLine)} onClick={blockTopClick} />
      <div className={clsx(iframeStyle.blockLine, iframeStyle.bottomLine)} />

      {isReady && (
        <PlayerControls
          isHideControls={isHideControls}
          isFullScreen={isFullScreen}
          toggleFullScreen={toggleFullScreen}
          isMuted={isMuted}
          toggleMute={handleClickToggle}
          setVolume={setVolume}
          isNotTransparent
        />
      )}
      {isReady && isWebinarStarted && (isMuted || isVideoBug) && (
        <TranslationSoundButton
          isPaused={!isMuted}
          modificator={clsx({ [iframeStyle.buttonPassEvent]: isVideoBug })}
          onClick={handleClickToggle}
        />
      )}
    </TranslationPlayer>
  )
}

export default VkTranslation
